<script setup lang="ts">
    import { computed } from 'vue'
    import { useImageFallback } from 'composables'

    /**
     * Every avatar is a tile, a rounded square with its own colors: a person,
     * an organization and an agent alike (DESIGN.md, principle 13). `modifiers`
     * carries a size (`md`, `lg`) or `word`, the dark ground that marks the
     * signed-in user; the tile paints every other ground itself.
     */
    const props = withDefaults(
        defineProps<{
            name?: string
            imgSrc?: string | null
            modifiers?: string
            /** Shown when there is no image (or it is broken) and no name. */
            fallbackIcon?: string
        }>(),
        {
            name: undefined,
            imgSrc: undefined,
            modifiers: undefined,
            fallbackIcon: 'ri:user-line',
        },
    )

    // OAuth avatar urls expire and uploaded logos get deleted: without this the
    // browser paints its broken-image glyph inside the tile. Keying by url
    // also means a new/refreshed src is retried instead of staying broken.
    const { canShowImage, onImageError } = useImageFallback()
    const hasImage = computed(() => canShowImage(props.imgSrc))

    // `transparent` only under an image, so the initials always get a ground
    const avatarModifiers = computed(() =>
        hasImage.value ? 'transparent' : props.modifiers,
    )

    const initials = computed(() => {
        if (!props.name) {
            return ''
        }
        // Only words that start with a letter or a digit: "[bench] S1" gave
        // "[S" and "CAI - Marche" gave "C-"
        return props.name
            .split(/\s+/)
            .filter((word) => /^[\p{L}\p{N}]/u.test(word))
            .slice(0, 2)
            .map((word) => word.charAt(0).toUpperCase())
            .join('')
    })
</script>

<template>
    <VvAvatar
        class="pk-avatar"
        :img-src="hasImage ? (imgSrc as string) : undefined"
        :modifiers="avatarModifiers">
        <template #default>
            <img
                v-if="hasImage"
                :src="imgSrc as string"
                alt="avatar"
                @error="onImageError(imgSrc)" />
            <template v-else-if="initials">{{ initials }}</template>
            <VvIcon v-else-if="fallbackIcon" :name="fallbackIcon" />
        </template>
    </VvAvatar>
</template>

<style lang="scss">
    .pk-avatar {
        // Users can upload any image shape: always crop it to the tile
        // (the inner img inherits object-fit from the avatar root)
        object-fit: cover;
        overflow: hidden;

        // The corner grows with the tile, a fifth of its side: 6px on the
        // 28px of a list, 8px at 40, 13px on the 64px logo of the catalog
        // page. A fixed corner made a large tile read as a square and a
        // small one as a circle. It keeps it inside a field too: the 2px
        // that ran parallel to the field's corner read as a square.
        &,
        > img {
            border-radius: 20%;
        }

        // The placeholder icon should fill the tile like the initials it
        // replaces, not sit in it as a tiny glyph
        > .vv-icon {
            font-size: 1.4em;
        }

        // Initials sit on a filled tile framed by a hairline, in bold: an
        // outline in the text color reads as a placeholder. An image goes
        // without the hairline: drawn around a logo it read as a second
        // shape, a square around a round mark.
        &:not(.vv-avatar--transparent, .vv-avatar--word) {
            background-color: var(--color-surface-3);
            color: var(--color-word-2);
        }

        &:not(.vv-avatar--transparent) {
            box-shadow: 0 0 0 var(--spacing-px) var(--color-surface-4);
            font-weight: var(--font-bold);
        }
    }
</style>
